import { z } from 'zod';
import {
  FEATURE_FLAGS,
  type FeatureFlagKey,
  type PlanFeatureFlagKey,
} from '../featureFlags/featureFlagSchema';
import { assertTypes, type NoNewFields } from '../primitives';

/**
 * Restaurant plans: the bundle of products and features OrderHub sells a venue (restaurant).
 *
 * - Storage: the `restaurantPlans` table holds at most one row per venue: its `brand`, and its plan
 *   (`planKey: null` is no plan). No row is OrderHub and no plan. The venue's `featureFlags` rows
 *   stay the only on/off truth. The plan decides which features venue staff see and change, and
 *   what a plan change turns on and off.
 * - Brand: a sign-up link (tripleshot.com, orderhub.com.au) will set it. Until then super admins
 *   set it (`restaurantPlans.set`), and a venue whose brand isn't set is OrderHub
 *   (`getRestaurantPlanBrand`).
 * - Default plan: plan pickers start on the venue's plan, or without one on its brand's default
 *   plan (`BRANDS`). Picking another brand starts on that brand's default plan.
 * - Products follow the plan: venue staff never turn one on or off. They turn the plan's features
 *   on or off (`getRestaurantPlanStaffFeatureFlagKeys`).
 * - Who sets the plan:
 *   - Tripleshot, whose plans are billed through Stripe (Tripleshot Billing): venue staff pick their
 *     brand's plan on the Features page, and pay for it by Stripe subscription
 *     (`billing.tripleshot.subscribe`, `billing.tripleshot.changePlan`). Any of the venue's users can,
 *     since roles aren't enforced yet. While the venue has a subscription (`stripeSubscriptionId`),
 *     only the subscription changes its plan: the billing webhook mirrors it onto the row
 *     (`billing/tripleshot/_syncSubscription.ts`), and super admins are refused.
 *   - Every other venue (OrderHub pays by direct debit outside the app; Zest's plan is free), and a
 *     Tripleshot venue without a subscription: super admins set the plan (`restaurantPlans.set`).
 * - Flags beyond the plan (online ordering on an OrderHub plan, say): super admins turn them on by
 *   hand. Plans sell no add-ons. A Zest venue's custom domain is the exception: its Zest Billing
 *   subscription turns it on (`zestBillingSchema.ts`).
 * - Stripe billing: a brand that bills through Stripe has its own billing account, separate from its
 *   Connect account. Tripleshot Billing (`tripleshotBilling`) bills plans. Zest Billing
 *   (`zestBilling`) bills SMS packs and custom domains, never a plan (`zestBillingSchema.ts`).
 *   - One plan subscription per Tripleshot venue. Its one item is the plan, whose price's
 *     `lookup_key` is the plan key (`getRestaurantPlanKeyFromPriceLookupKeys`), never a stored
 *     price id. Prices live in Stripe, never here.
 *   - Every subscription gets the one 10% AU GST tax rate.
 *   - A subscription that is `active`, `trialing` or `past_due` sets the plan: a failed payment
 *     keeps it while Stripe retries. After a subscription ends, or in any other status, the venue
 *     has no plan.
 *   - The Customer Portal handles cards, invoices and cancelling at the end of the period. It never
 *     changes the plan.
 * - Plan change (first plan, upgrade, downgrade, no plan): flags the new plan includes and the old
 *   one didn't turn on; flags the old plan included and the new one doesn't turn off, even if
 *   OrderHub turned them on by hand; every other flag stays as it was
 *   (`getRestaurantPlanChangeFeatureFlagKeys`).
 * - Editing a plan's flags ships a migration that applies the same change to the plan's venues.
 *   It hardcodes the added and removed keys, since this registry only knows the new list.
 * - Dropping a plan: move its venues off it first. `planKey` is the literal union (plans never
 *   reach the POS), so a deploy without a plan that venues still hold fails schema validation.
 * - Only products and features go in plans; the compiler rejects any other flag.
 * - Checked by `restaurantPlanSchema.test.ts`:
 *   - Every plan includes at least one product, and the product each of its flags requires.
 *   - OrderHub and Tripleshot plans always include POS. Zest plans always include online ordering
 *     and never POS.
 *   - Every brand's default plan is one of its own plans.
 *   - Every plan is found again from its price's lookup key.
 */

/** The brands OrderHub sells under, in the order super admins see them. */
export const BRAND_KEYS = ['orderhub', 'tripleshot', 'zest'] as const;
export const zBrandKey = z.literal(BRAND_KEYS);
export type BrandKey = z.output<typeof zBrandKey>;

/** The brand of a venue whose brand isn't set yet. */
export const DEFAULT_BRAND_KEY: BrandKey = 'orderhub';

/**
 * Every brand's name, and its default plan: the plan pickers start on for a venue without a plan.
 */
export const BRANDS: Record<BrandKey, { label: string; defaultPlanKey: RestaurantPlanKey }> = {
  /** Australia only (.com.au). */
  orderhub: { label: 'OrderHub', defaultPlanKey: 'orderhub-pro' },
  /** Global (.com). */
  tripleshot: { label: 'Tripleshot', defaultPlanKey: 'tripleshot-basic' },
  /** Global (.com). */
  zest: { label: 'Zest', defaultPlanKey: 'zest-free' },
};

export const RESTAURANT_PLAN_KEYS = [
  'orderhub-basic',
  'orderhub-pro',
  'tripleshot-basic',
  'tripleshot-pro',
  'zest-free',
] as const;
export const zRestaurantPlanKey = z.literal(RESTAURANT_PLAN_KEYS);
export type RestaurantPlanKey = z.output<typeof zRestaurantPlanKey>;

/**
 * Every plan's brand, name and the flags it includes. Changing a plan's flags needs a migration
 * (see above).
 */
export const RESTAURANT_PLANS: Record<
  RestaurantPlanKey,
  { brand: BrandKey; label: string; featureFlagKeys: PlanFeatureFlagKey[] }
> = {
  'orderhub-basic': {
    brand: 'orderhub',
    label: 'OrderHub Basic',
    featureFlagKeys: ['pos', 'pos-button-colors'],
  },
  'orderhub-pro': {
    brand: 'orderhub',
    label: 'OrderHub Pro',
    featureFlagKeys: ['pos', 'pos-button-colors', 'integrated-eftpos-australia'],
  },
  // Online ordering is on a tripleshot.site subdomain
  'tripleshot-basic': {
    brand: 'tripleshot',
    label: 'Tripleshot Basic',
    featureFlagKeys: ['pos', 'online-ordering', 'pos-button-colors'],
  },
  'tripleshot-pro': {
    brand: 'tripleshot',
    label: 'Tripleshot Pro',
    featureFlagKeys: ['pos', 'online-ordering', 'pos-button-colors', 'integrated-eftpos-australia'],
  },
  'zest-free': {
    brand: 'zest',
    label: 'Zest Free',
    // Pause online orders joins once it graduates from experimental
    featureFlagKeys: ['online-ordering'],
  },
};

/** The venue's brand: its row's, or OrderHub (`DEFAULT_BRAND_KEY`) while it isn't set. */
export const getRestaurantPlanBrand = (
  restaurantPlan: Pick<RestaurantPlan, 'brand'> | null,
): BrandKey => (restaurantPlan === null ? DEFAULT_BRAND_KEY : restaurantPlan.brand);

/** The products and features a plan includes. `null` (no plan) includes none. */
export const getRestaurantPlanFeatureFlagKeys = (
  planKey: RestaurantPlanKey | null,
): readonly FeatureFlagKey[] => (planKey === null ? [] : RESTAURANT_PLANS[planKey].featureFlagKeys);

/**
 * The flags venue staff turn on or off on a plan: its features. Products follow the plan, so staff
 * never change them.
 */
export const getRestaurantPlanStaffFeatureFlagKeys = (
  planKey: RestaurantPlanKey | null,
): readonly FeatureFlagKey[] =>
  getRestaurantPlanFeatureFlagKeys(planKey).filter((key) => FEATURE_FLAGS[key].type === 'feature');

/** The flags a plan change turns on and off. Every other flag stays as it was. */
export const getRestaurantPlanChangeFeatureFlagKeys = (
  fromPlanKey: RestaurantPlanKey | null,
  toPlanKey: RestaurantPlanKey | null,
): { turnedOnKeys: FeatureFlagKey[]; turnedOffKeys: FeatureFlagKey[] } => {
  const fromKeys = getRestaurantPlanFeatureFlagKeys(fromPlanKey);
  const toKeys = getRestaurantPlanFeatureFlagKeys(toPlanKey);
  return {
    turnedOnKeys: toKeys.filter((key) => !fromKeys.includes(key)),
    turnedOffKeys: fromKeys.filter((key) => !toKeys.includes(key)),
  };
};

/**
 * The plan a subscription sells, from its items' price lookup keys: its one item is the plan, and
 * a plan's price's lookup key is its plan key.
 * - Throws unless there is exactly one lookup key and it's a plan key.
 */
export const getRestaurantPlanKeyFromPriceLookupKeys = (
  lookupKeys: readonly string[],
): RestaurantPlanKey => {
  const planKey = RESTAURANT_PLAN_KEYS.find((key) => key === lookupKeys[0]);
  if (planKey === undefined || lookupKeys.length > 1) {
    throw new Error(
      `Expected exactly one plan price, got lookup keys: ${lookupKeys.join(', ') || 'none'}`,
    );
  }
  return planKey;
};

/** One venue's brand and plan. See the rules above. */
export const restaurantPlanSchema = z.object({
  restaurantId: z.string(),
  /** The brand OrderHub sells to the venue under. Only its plans are offered. */
  brand: zBrandKey,
  /** `null`: no plan. */
  planKey: zRestaurantPlanKey.nullable(),
  /** The venue's subscription on the Tripleshot Billing account, or `null` without one. */
  stripeSubscriptionId: z.string().nullable(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type RestaurantPlan = z.output<typeof restaurantPlanSchema>;

/** Strict schema */
export const restaurantPlanStrictSchema = restaurantPlanSchema;

/** Convex schema */
export const restaurantPlanCvxSchema = restaurantPlanSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type RestaurantPlanCvx = z.output<typeof restaurantPlanCvxSchema>;
assertTypes<NoNewFields<RestaurantPlan, RestaurantPlanCvx>>();
